import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Video, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/api";
import { trackEvent } from "@/stats";
import { trackGAEvent } from "@/consent";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { SITE } from "@/config";

export default function ContactsPage() {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: "", contact: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const [cform, setCform] = useState({ name: "", email: "", message: "" });
  const [csending, setCsending] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    setCsending(true);
    try {
      await api.post("/contact", { ...cform, lang });
      toast.success(t.contactform.success);
      setCform({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err) || t.contactform.error);
    } finally {
      setCsending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/bookings", { ...form, lang });
      toast.success(t.contacts.form_success);
      setForm({ name: "", contact: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err) || t.contacts.form_error);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-28 pb-20" data-testid="contacts-page">
      <Seo page="contacts" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-terra mb-4">{t.contacts.eyebrow}</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight" data-testid="contacts-title">{t.contacts.title}</h1>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="space-y-6">
              <div className="flex gap-4 items-start">
                <span className="w-11 h-11 rounded-xl bg-sand flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-terra" /></span>
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-muted2 mb-1">{t.contacts.city_label}</p>
                  <p className="font-medium" data-testid="contact-city">{SITE.city[lang]}</p>
                </div>
              </div>
              {SITE.email && (
                <div className="flex gap-4 items-start">
                  <span className="w-11 h-11 rounded-xl bg-sand flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-terra" /></span>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-muted2 mb-1">E-mail</p>
                    <a href={`mailto:${SITE.email}`} className="font-medium hover:text-terra" data-testid="contact-email">{SITE.email}</a>
                  </div>
                </div>
              )}
              <div className="flex gap-4 items-start">
                <span className="w-11 h-11 rounded-xl bg-sand flex items-center justify-center shrink-0"><Video className="w-5 h-5 text-terra" /></span>
                <p className="font-medium pt-2.5" data-testid="contact-online">{t.contacts.online}</p>
              </div>
              <div className="rounded-2xl overflow-hidden border border-line shadow-sm mt-4">
                <iframe
                  title="map"
                  src="https://maps.google.com/maps?q=Praha%2C%20Czech%20Republic&output=embed"
                  className="w-full h-64"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
              {SITE.ifmvUrl && (
                <a
                  href={SITE.ifmvUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { trackEvent("booking_ifmv_click", { lang }); trackGAEvent("booking_click", { language: lang, page_path: window.location.pathname, booking_provider: "IFMV", link_url: SITE.ifmvUrl }); }}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-white px-6 py-5 shadow-sm transition-colors hover:border-sage"
                  data-testid="contact-ifmv-link"
                >
                  <span className="text-sm font-semibold">{t.booking.ifmv_cta}</span>
                  <ExternalLink className="w-4 h-4 text-terra shrink-0" />
                </a>
              )}
            </div>
          </Reveal>
          <div className="space-y-8">
          <Reveal delay={0.1}>
            <div className="bg-white rounded-3xl border border-line p-8 sm:p-10 shadow-sm" data-testid="contact-form-card">
              <h2 className="font-serif text-2xl font-semibold mb-6">{t.contacts.form_title}</h2>
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <Input required placeholder={t.contacts.form_name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
                <Input required placeholder={t.contacts.form_contact} value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="contact-contact-input" />
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger data-testid="contact-service-select">
                    <SelectValue placeholder={t.contacts.form_service} />
                  </SelectTrigger>
                  <SelectContent>
                    {t.contacts.consult_types.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea rows={4} placeholder={t.contacts.form_message} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
                <button type="submit" disabled={sending} data-testid="contact-submit-button" className="w-full rounded-full bg-sage py-3.5 text-sm font-semibold text-white transition-colors hover:bg-ink disabled:opacity-60">
                  {sending ? "…" : t.contacts.form_send}
                </button>
                <p className="text-xs text-muted2 leading-relaxed pt-1" data-testid="contact-consent-note">
                  {t.privacy.consentNote}{" "}
                  <Link to={`/${lang}/privacy`} className="underline underline-offset-2 text-terra hover:text-ink transition-colors">{t.privacy.linkInText}</Link>.
                </p>
              </form>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-white rounded-3xl border border-line p-8 sm:p-10 shadow-sm" data-testid="contact-simple-card">
              <h2 className="font-serif text-2xl font-semibold mb-2">{t.contactform.title}</h2>
              <p className="text-sm text-muted2 mb-6">{t.contactform.subtitle}</p>
              <form onSubmit={submitContact} className="space-y-4" data-testid="contact-simple-form">
                <Input required maxLength={100} placeholder={t.contactform.name} value={cform.name} onChange={(e) => setCform({ ...cform, name: e.target.value })} data-testid="contact-simple-name" />
                <Input required type="email" maxLength={200} placeholder={t.contactform.email} value={cform.email} onChange={(e) => setCform({ ...cform, email: e.target.value })} data-testid="contact-simple-email" />
                <Textarea required rows={4} maxLength={3000} placeholder={t.contactform.message} value={cform.message} onChange={(e) => setCform({ ...cform, message: e.target.value })} data-testid="contact-simple-message" />
                <button type="submit" disabled={csending} data-testid="contact-simple-send" className="w-full rounded-full bg-sage py-3.5 text-sm font-semibold text-white transition-colors hover:bg-ink disabled:opacity-60">
                  {csending ? "…" : t.contactform.send}
                </button>
                <p className="text-xs text-muted2 leading-relaxed pt-1" data-testid="contact-simple-consent-note">
                  {t.privacy.consentNote}{" "}
                  <Link to={`/${lang}/privacy`} className="underline underline-offset-2 text-terra hover:text-ink transition-colors">{t.privacy.linkInText}</Link>.
                </p>
              </form>
            </div>
          </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
}
